import { Box, Flex, forwardRef, Heading, HTMLChakraProps, Image, Skeleton, Text } from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ThinEvent } from '@/resources/ThinEvent';

const EventDurationWithTimezone = dynamic(
  () =>
    import('@/components/ui/shared/event/EventDurationWithTimezone/EventDurationWithTimezone').then(
      (mod) => mod.EventDurationWithTimezone
    ),
  {
    ssr: false,
  }
);

interface IFeaturedEventCardProps extends HTMLChakraProps<'div'> {
  event: ThinEvent;
  footer: ReactNode;
}

export const FeaturedEventCard = forwardRef<IFeaturedEventCardProps, 'div'>(function FeaturedEventCard(props, ref) {
  const { event, footer, ...rest } = props;

  return (
    <Flex
      flexDir="row"
      minH={{ base: '292px', lg: '400px' }}
      maxH="400px"
      _hover={{ textDecor: 'none' }}
      ref={ref}
      {...rest}
    >
      <Image src={event.mediumHeaderImageUrl} alt="" fallback={<Skeleton size="full" />} />

      <Flex flexDir="column" py={{ base: 6, lg: 10 }} px={{ base: 8, lg: 14 }} backgroundColor="white" w="full">
        <Box>
          <Image
            src={event.smallLogoUrl}
            alt="logo"
            bg="white"
            w={{ base: '40px', lg: '56px' }}
            mb={{ base: 4, lg: 6 }}
            fallback={<Box bg="gray.200" w="full" borderRadius="md" />}
          />
        </Box>

        <Heading
          as="h3"
          mb={{ base: 3, lg: 5 }}
          fontSize={{ base: '2xl', lg: '4xl' }}
          lineHeight={{ base: '1.875rem', lg: '2.75rem' }}
          noOfLines={2}
        >
          {event.name}
        </Heading>
        {event.shortDescription && <Text noOfLines={2}>{event.shortDescription}</Text>}

        {footer}
      </Flex>
    </Flex>
  );
});

export type FeaturedUpcomingEventCardProps = Omit<IFeaturedEventCardProps, 'footer'>;

export const FeaturedUpcomingEventCard = forwardRef<FeaturedUpcomingEventCardProps, typeof FeaturedEventCard>(
  (props, ref) => {
    const { event, ...rest } = props;
    const { t } = useTranslation();

    return (
      <FeaturedEventCard
        event={event}
        ref={ref}
        footer={
          <Box mt={8}>
            <EventDurationWithTimezone event={event} />
          </Box>
        }
        {...rest}
      />
    );
  }
);

export type FeaturedPastEvenCardProps = Omit<IFeaturedEventCardProps, 'footer'>;

export type FeaturedEventCardFallbackProps = HTMLChakraProps<'div'>;
